import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  Storefront, UploadSimple, X, Target,
  Export, FilePdf, FileXls,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import PeriodFilter from "../components/PeriodFilter";
import KpiCard from "../components/KpiCard";
import EmptyState from "../components/EmptyState";
import SortControl from "../components/SortControl";
import TrendCharts from "../components/TrendCharts";
import PerfTable from "../components/PerfTable";
import FloorTargetBoard from "../components/FloorTargetBoard";
import { computeRanks } from "../components/RankBadge";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";
import api from "../lib/api";
import { exportExcel, exportPdf, sortRows } from "../lib/exporters";
import { fmtCurrency, fmtNumber, fmtDecimal, fmtDate, hasPerm, PERIOD_LABELS } from "../lib/format";
import { useAuth } from "../context/AuthContext";

const FILTER_META = {
  floor: { label: "Floor", color: "bg-blue-100 text-blue-700 ring-blue-200" },
  category: { label: "Category", color: "bg-violet-100 text-violet-700 ring-violet-200" },
  salesman: { label: "Salesman", color: "bg-emerald-100 text-emerald-700 ring-emerald-200" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const showAmount = hasPerm(user, "view_sales_amount");
  const canFloor = hasPerm(user, "view_floor_dashboard");
  const canCategory = hasPerm(user, "view_category_dashboard");
  const canSalesman = hasPerm(user, "view_salesman_dashboard");

  const [period, setPeriod] = useState("today");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [meta, setMeta] = useState(null);
  const [overview, setOverview] = useState(null);
  const [floors, setFloors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [floorTargets, setFloorTargets] = useState(null);
  // Cross-filter selections (peers, AND-intersection) — LOCAL to the 3 tables only
  const [sel, setSel] = useState({ floor: null, category: null, salesman: null });
  const [loading, setLoading] = useState(true);
  const [tablesBusy, setTablesBusy] = useState(false);
  const [floorSort, setFloorSort] = useState({ key: "sales", order: "desc" });
  const [categorySort, setCategorySort] = useState({ key: "sales", order: "desc" });
  const [salesmanSort, setSalesmanSort] = useState({ key: "abv", order: "desc" });
  const [exporting, setExporting] = useState(false);
  const canExport = hasPerm(user, "export_dashboard");
  const selRef = useRef(sel);
  selRef.current = sel;

  useEffect(() => {
    api.get("/dashboard/meta").then((r) => setMeta(r.data)).catch(() => {});
    if (canFloor) {
      api.get("/dashboard/floor-targets").then((r) => setFloorTargets(r.data)).catch(() => {});
    }
  }, [canFloor]);

  const periodParams = useCallback(() => {
    const p = { period };
    if (period === "custom") { p.start = custom.start; p.end = custom.end; }
    return p;
  }, [period, custom]);

  const filterParams = useCallback(() => {
    const p = periodParams();
    if (sel.floor) p.floor = sel.floor;
    if (sel.category) p.category = sel.category;
    if (sel.salesman) p.salesman = sel.salesman;
    return p;
  }, [periodParams, sel]);

  // KPI cards + trends context: period only, NEVER affected by table cross-filters.
  const loadOverview = useCallback(async () => {
    if (period === "custom" && (!custom.start || !custom.end)) return;
    setLoading(true);
    try {
      const r = await api.get("/dashboard/analysis", { params: periodParams() });
      setOverview(r.data.overview);
    } catch {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [period, custom, periodParams]);

  // The three performance tables: honor cross-filters. Silent (no skeleton) so the
  // page never jumps / loses scroll when a selection changes.
  const loadTables = useCallback(async () => {
    if (period === "custom" && (!custom.start || !custom.end)) return;
    setTablesBusy(true);
    try {
      const r = await api.get("/dashboard/analysis", { params: filterParams() });
      setFloors(r.data.floors || []);
      setCategories(r.data.categories || []);
      setSalesmen(r.data.salesmen || []);
    } catch {
      toast.error("Failed to load performance tables");
    } finally {
      setTablesBusy(false);
    }
  }, [period, custom, filterParams]);

  // On period change: reset selections + reload KPIs.
  useEffect(() => {
    setSel({ floor: null, category: null, salesman: null });
    loadOverview();
  }, [period, custom, loadOverview]);

  // On period OR selection change: reload the three tables.
  useEffect(() => { loadTables(); }, [loadTables]);

  const toggle = (dim, name) => setSel((s) => ({ ...s, [dim]: s[dim] === name ? null : name }));
  const clearAll = () => setSel({ floor: null, category: null, salesman: null });
  const clearOne = (dim) => setSel((s) => ({ ...s, [dim]: null }));

  const activeFilters = Object.entries(sel).filter(([, v]) => v);
  const hasFilters = activeFilters.length > 0;

  const periodLabel = PERIOD_LABELS[period];
  const noData = overview && overview.bills === 0 && overview.net_qty === 0;

  const exportParams = () => {
    const p = { period, sort: salesmanSort.key, order: salesmanSort.order };
    if (period === "custom") { p.start = custom.start; p.end = custom.end; }
    if (sel.floor) p.floor = sel.floor;
    if (sel.category) p.category = sel.category;
    if (sel.salesman) p.salesman = sel.salesman;
    return p;
  };
  const doExcel = async () => {
    setExporting(true);
    try { await exportExcel(exportParams()); toast.success("Excel exported"); }
    catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };
  const doPdf = async () => {
    setExporting(true);
    const t = toast.loading("Preparing PDF…");
    try {
      await exportPdf("dashboard-capture", `SS_Sales_Analytics_${period}.pdf`);
      toast.success("PDF exported", { id: t });
    } catch (e) { toast.error(e.message || "PDF export failed", { id: t }); }
    finally { setExporting(false); }
  };

  const sortedFloors = sortRows(floors, floorSort.key, floorSort.order);
  const sortedCategories = sortRows(categories, categorySort.key, categorySort.order);
  const sortedSalesmen = sortRows(salesmen, salesmanSort.key, salesmanSort.order);
  const floorRanks = computeRanks(floors, ["net_sales", "abv", "abq", "bills"]);
  const categoryRanks = computeRanks(categories, ["net_sales", "bills"]);
  const salesmanRanks = computeRanks(salesmen, ["net_sales", "abv", "abq", "bills"]);

  const showFloorTargetBoard = canFloor && floorTargets && (floorTargets.floor_targets || []).length > 0;

  if (meta && !meta.has_data) {
    return (
      <Layout title="Dashboard">
        <EmptyState
          icon={UploadSimple}
          testId="dashboard-empty"
          title="No sales uploaded yet"
          message="Upload your first daily POS Excel export to see performance metrics."
          action={
            hasPerm(user, "upload_data") ? (
              <button
                onClick={() => navigate("/upload")}
                data-testid="empty-upload-btn"
                className="rounded-md bg-blue-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-800"
              >
                Upload Sales Data
              </button>
            ) : null
          }
        />
      </Layout>
    );
  }

  return (
    <Layout title="Dashboard">
      <div className="animate-fade-in space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Storefront size={16} weight="bold" /> Overall Store · {periodLabel}
            </div>
            {overview && (
              <p className="text-xs text-slate-400">
                {period === "custom" ? `${fmtDate(custom.start)} — ${fmtDate(custom.end)}` : ""}
              </p>
            )}
          </div>
          {canExport && !noData && overview && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="export-btn"
                  disabled={exporting}
                  className="flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                >
                  <Export size={18} weight="bold" /> {exporting ? "Exporting…" : "Export"}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem onClick={doPdf} data-testid="export-pdf">
                  <FilePdf size={18} weight="bold" className="mr-2 text-red-600" /> Export as PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={doExcel} data-testid="export-excel">
                  <FileXls size={18} weight="bold" className="mr-2 text-emerald-600" /> Export as Excel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <PeriodFilter period={period} setPeriod={setPeriod} custom={custom} setCustom={setCustom} showMonthly={hasPerm(user, "view_monthly_data")} />

        {loading ? (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-28 animate-pulse rounded-lg border border-slate-200 bg-white" />
            ))}
          </div>
        ) : noData ? (
          <EmptyState title={`No data for ${periodLabel}`} message="There are no sales records for this period. Try another date range." testId="no-period-data" />
        ) : overview ? (
          <div id="dashboard-capture" className="space-y-6">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <KpiCard testId="kpi-net-sales" big label="Net Sales" value={showAmount ? fmtCurrency(overview.net_sales) : "—"} sub={showAmount ? `Gross ${fmtCurrency(overview.sales_amount)}` : "Hidden"} accent />
              <KpiCard testId="kpi-abv" label="ABV" value={fmtCurrency(overview.abv, true)} sub="Net Sales / Bills" />
              <KpiCard testId="kpi-abq" label="ABQ" value={fmtDecimal(overview.abq)} sub="Net Qty / Bills" />
              <KpiCard testId="kpi-total-sales" label="Total Sales" value={showAmount ? fmtCurrency(overview.sales_amount) : "—"} sub="Gross sales" />
              <KpiCard testId="kpi-bills" label="Total Bills" value={fmtNumber(overview.bills)} sub="Unique bills" />
              <KpiCard testId="kpi-qty" label="Total Quantity" value={fmtNumber(overview.sales_qty)} sub="Items sold" />
              <KpiCard testId="kpi-returns" label="Returns" value={showAmount ? fmtCurrency(overview.return_amount) : "—"} sub={`${fmtNumber(overview.return_qty)} items returned`} />
              <KpiCard testId="kpi-net-qty" label="Net Quantity" value={fmtNumber(overview.net_qty)} sub="Sales − Returns" />
            </div>

            <TrendCharts />

            {showFloorTargetBoard && (
              <div className="space-y-2" data-testid="admin-floor-target-section">
                <h3 className="flex items-center gap-2 font-display text-base font-bold text-slate-800">
                  <Target size={18} weight="bold" className="text-blue-700" /> Floor Target Board
                  <span className="text-xs font-normal text-slate-400">· this month through yesterday</span>
                </h3>
                <FloorTargetBoard mode="admin" data={floorTargets} />
              </div>
            )}

            {/* Interactive performance tables — cross-filter is LOCAL to this block */}
            <div className={`space-y-6 transition-opacity ${tablesBusy ? "opacity-60" : ""}`} data-testid="performance-tables">
              <div className="flex flex-wrap items-center gap-2" data-testid="active-filters">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Selected:</span>
                {!hasFilters && <span className="text-sm font-medium text-slate-500" data-testid="filters-all">All</span>}
                {activeFilters.map(([dim, val]) => (
                  <button
                    key={dim}
                    onClick={() => clearOne(dim)}
                    title="Remove this filter"
                    data-testid={`filter-chip-${dim}`}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold ring-1 ${FILTER_META[dim].color}`}
                  >
                    <span className="text-[10px] font-bold uppercase opacity-60">{FILTER_META[dim].label}</span>
                    {val}
                    <X size={13} weight="bold" />
                  </button>
                ))}
                {hasFilters && (
                  <button
                    onClick={clearAll}
                    data-testid="clear-all-btn"
                    className="ml-1 rounded-full border border-slate-300 px-3 py-1 text-sm font-semibold text-slate-600 hover:bg-slate-100"
                  >
                    Clear All
                  </button>
                )}
              </div>

              {canFloor && (
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="font-display text-base font-bold text-slate-800">Floor Performance</h3>
                    <SortControl sortKey={floorSort.key} setSortKey={(k) => setFloorSort((s) => ({ ...s, key: k }))} order={floorSort.order} setOrder={(o) => setFloorSort((s) => ({ ...s, order: o }))} idPrefix="floor" />
                  </div>
                  <PerfTable
                    variant="floor"
                    nameLabel="Floor"
                    testId="floor-table"
                    rows={sortedFloors}
                    ranks={floorRanks}
                    showAmount={showAmount}
                    onRowClick={(r) => toggle("floor", r.name)}
                    activeName={sel.floor}
                    emptyMsg="No matching data for the selected combination."
                  />
                </div>
              )}

              {canCategory && (
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="font-display text-base font-bold text-slate-800">Category Performance</h3>
                    <SortControl sortKey={categorySort.key} setSortKey={(k) => setCategorySort((s) => ({ ...s, key: k }))} order={categorySort.order} setOrder={(o) => setCategorySort((s) => ({ ...s, order: o }))} idPrefix="category" />
                  </div>
                  <PerfTable
                    variant="category"
                    nameLabel="Category"
                    testId="category-table"
                    rows={sortedCategories}
                    ranks={categoryRanks}
                    showAmount={showAmount}
                    onRowClick={(r) => toggle("category", r.name)}
                    activeName={sel.category}
                    emptyMsg="No matching data for the selected combination."
                  />
                </div>
              )}

              {canSalesman && (
                <div id="salesman-section" className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="font-display text-base font-bold text-slate-800">Salesman Performance</h3>
                    <SortControl sortKey={salesmanSort.key} setSortKey={(k) => setSalesmanSort((s) => ({ ...s, key: k }))} order={salesmanSort.order} setOrder={(o) => setSalesmanSort((s) => ({ ...s, order: o }))} idPrefix="salesman" />
                  </div>
                  <PerfTable
                    variant="salesman"
                    nameLabel="Salesman"
                    testId="salesman-table"
                    rows={sortedSalesmen}
                    ranks={salesmanRanks}
                    showAmount={showAmount}
                    onRowClick={(r) => toggle("salesman", r.name)}
                    activeName={sel.salesman}
                    emptyMsg="No matching data for the selected combination."
                  />
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </Layout>
  );
}
